<template>
  <VDropdown
    v-if="withPreview"
    :triggers="['hover']"
    :popperTriggers="['hover']"
    :disabled="!withPreview"
    :delay="{ show: 500, hide: 150 }"
  >
    <LocaleLink ref="el" class="relative" :href="href" v-bind="$attrs">
      <slot />
    </LocaleLink>
    <template #popper="{ hide }">
      <PreviewComponent
        v-if="legalTextParagraphId"
        :paragraphId="legalTextParagraphId"
        :linkType="linkType"
        :parentEl="el?.element"
        @close="hide"
      />
    </template>
  </VDropdown>
  <LocaleLink v-else ref="el" class="relative" :href="href" v-bind="$attrs">
    <slot />
  </LocaleLink>
</template>

<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<script lang="ts" setup>
import type { IBookmark } from "~/models/ILegalTextParagraph";
import type { ILinkType } from "~/models/ILinkType";
import type { IUserEntry } from "~/models/IUserEntry";
import LocaleLink from "~/components/links/LocaleLink.vue";
import { clientOnly } from "vike-vue/clientOnly";

const PreviewComponent = clientOnly(
  () => import("~/components/legaltextparagraph/paragraph/ParagraphPreview.vue")
);

type PrefetchOptions = {
  enabled: boolean;
  delay: number;
  immediate: boolean;
};

type Props = {
  legalText?: string;
  legalTextParagraph?: string;
  legalTextParagraphId?: number;
  oldVersionId?: number;
  linkType?: ILinkType;
  withPreview?: boolean;
  entry?: IUserEntry | IBookmark;
  prefetch?: Partial<PrefetchOptions>;
};

const defaultPrefetchOptions: PrefetchOptions = {
  enabled: true,
  delay: 0,
  immediate: false,
};

const props = withDefaults(defineProps<Props>(), {
  legalText: undefined,
  legalTextParagraph: undefined,
  legalTextParagraphId: undefined,
  oldVersionId: undefined,
  linkType: undefined,
  entry: undefined,
  prefetch: () => ({ enabled: true, delay: 0, immediate: false }),
});

const { legalText, legalTextParagraph, entry, oldVersionId } = toRefs(props);

const prefetch = computed(() => ({
  ...props.prefetch,
  ...defaultPrefetchOptions,
}));

const el = useTemplateRef<{ element: HTMLAnchorElement }>("el");

const { href } = useParagraphLink({
  legalTextSlug: legalText,
  paragraphSlug: legalTextParagraph,
  oldVersionId,
  entry,
});

const isHovered = useElementHover(() => el.value?.element, {
  delayEnter: 200,
  delayLeave: 600,
});

usePrefetchParagraph(
  legalText,
  legalTextParagraph,
  logicAnd(
    () => prefetch.value.enabled,
    logicOr(() => prefetch.value.immediate, isHovered),
    () => legalTextParagraph.value && legalTextParagraph.value !== "toc"
  ),
  prefetch.value.delay
);
</script>
